/** Max CODE POINTS kept per value; longer values are truncated, never rejected. */
const UTM_MAX_LENGTH = 255;
/**
 * C0 controls + DEL — includes CR and LF (log injection).
 *
 * `no-control-regex` is disabled deliberately: that rule exists to catch control characters
 * that reached a pattern by ACCIDENT, and matching them is this pattern's entire purpose. The
 * backend strips the same class (`FormSubmission::normalizeUtm`), so narrowing or dropping it
 * here would silently desync the two layers.
 */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/g;
/**
 * Strip control chars -> trim -> blank-check -> truncate, in that order. Stripping first is
 * what makes a value of only control characters collapse to blank (and therefore be omitted)
 * instead of surviving as an empty-looking string.
 *
 * The cap counts CODE POINTS, not UTF-16 code units — hence `Array.from` (which iterates code
 * points) rather than `String.slice` (which cuts code units). Two reasons, and the first one is
 * load-bearing:
 *
 *  - `slice` can cut an astral character — an emoji, ordinary in a campaign name — mid-surrogate-
 *    pair, leaving a LONE high surrogate as the last unit. `JSON.stringify` serializes that as the
 *    escape `\ud83c`, and the backend decodes the body with `JSON_THROW_ON_ERROR`, turning the
 *    resulting `JsonException` into a 400. The donation would be refused over a marketing tag the
 *    donor never wrote — and a malformed UTM must never cost a donation, which is exactly why the
 *    backend has no validation rule for these values in the first place.
 *  - Code points are also the unit the two layers below count: the backend normalizes with
 *    `mb_substr($value, 0, 255)` and the column is `varchar(255)`, which PostgreSQL counts in
 *    characters. Unit-based truncation silently disagreed with both.
 */
function normalize(raw) {
    if (raw === null) {
        return undefined;
    }
    const cleaned = raw.replace(CONTROL_CHARS, "").trim();
    if (cleaned === "") {
        return undefined;
    }
    return Array.from(cleaned).slice(0, UTM_MAX_LENGTH).join("");
}
/**
 * Parse `utm_source` / `utm_medium` / `utm_campaign` out of a query string (with or without
 * the leading `?`). Keys are matched LOWERCASE only — every real campaign-link producer
 * emits them lowercase, and accepting arbitrary casing would silently widen the contract.
 * On a repeated key the FIRST occurrence wins (`URLSearchParams.get`).
 */
export function parseUtmParameters(search) {
    const params = new URLSearchParams(search);
    const utm = {};
    const source = normalize(params.get("utm_source"));
    if (source !== undefined) {
        utm.utmSource = source;
    }
    const medium = normalize(params.get("utm_medium"));
    if (medium !== undefined) {
        utm.utmMedium = medium;
    }
    const campaign = normalize(params.get("utm_campaign"));
    if (campaign !== undefined) {
        utm.utmCampaign = campaign;
    }
    return utm;
}
